/**
 * CircuitList page — searchable, sortable, filterable DataTable of data circuits.
 *
 * Requirements: 34.6, 34.7, 34.8
 */

import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { circuitService, type CircuitListParams } from '@/services/circuitService';
import { BandwidthAnalytics as BandwidthAnalyticsView } from './BandwidthAnalytics';
import type { DataCircuit, SortDirection } from '@/types';
import { UserRole } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

const LIFECYCLE_VARIANTS: Record<string, BadgeVariant> = {
  Ordered: 'blue',
  'In Progress': 'yellow',
  Installed: 'blue',
  Active: 'green',
  'Pending Disconnect': 'yellow',
  Disconnected: 'red',
};

export function CircuitList() {
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuth();
  const canWrite = user?.isAdmin ||
    user?.roles.some((r) => ['Admin', 'IT Admin', 'Contract Specialist'].includes(r));
  const canSeeFinancials = hasAnyRole([UserRole.Admin, UserRole.ContractSpecialist, UserRole.Controller, UserRole.CFO, UserRole.Accounting]);

  const [params, setParams] = useState<CircuitListParams>({
    page: 1,
    pageSize: 25,
    sortBy: 'circuitId',
    sortDir: 'desc',
  });

  const [siteFilter, setSiteFilter] = useState<string>('');
  const [carrierFilter, setCarrierFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [stoplights, setStoplights] = useState<Record<string, 'green' | 'yellow' | 'red'>>({});

  // Fetch stoplight data for all branches
  useEffect(() => {
    fetch('/api/branches/stoplight', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.branches) {
          const map: Record<string, 'green' | 'yellow' | 'red'> = {};
          for (const b of d.branches) map[b.siteNumber] = b.stoplight;
          setStoplights(map);
        }
      })
      .catch(() => {});
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['circuits', params, siteFilter, carrierFilter, statusFilter, typeFilter],
    queryFn: () =>
      circuitService.list({
        ...params,
        siteNumber: siteFilter || undefined,
        carrierVendor: carrierFilter || undefined,
        lifecycleStatus: statusFilter || undefined,
        circuitType: typeFilter || undefined,
      }),
  });

  const handleSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleSort = useCallback((key: string, direction: SortDirection) => {
    setParams((prev) => ({ ...prev, sortBy: key, sortDir: direction }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const columns: Column<DataCircuit & Record<string, unknown>>[] = [
    {
      key: 'circuitId',
      header: 'ID',
      sortable: true,
      width: '60px',
      render: (row) => (
        <Link
          to={`/circuits/${row.circuitId}`}
          style={{ color: '#c8102e', textDecoration: 'none', fontWeight: 500 }}
        >
          {row.circuitId}
        </Link>
      ),
    },
    {
      key: 'siteNumber',
      header: 'Site',
      sortable: true,
      width: '90px',
      render: (row) => {
        const light = stoplights[row.siteNumber?.trim()];
        const dotColor = light === 'red' ? '#dc2626' : light === 'yellow' ? '#d97706' : light === 'green' ? '#059669' : '#94a3b8';
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor, display: 'inline-block', flexShrink: 0 }} title={light ? `IT spend: ${light}` : 'No data'} />
            <Link to={`/sites/${row.siteNumber}`} style={{ color: '#c8102e', textDecoration: 'none' }}>
              {row.siteNumber}
            </Link>
          </span>
        );
      },
    },
    {
      key: 'circuitIdentifier',
      header: 'Circuit ID',
      sortable: false,
      render: (row) => row.circuitIdentifier ?? '—',
    },
    {
      key: 'carrierVendor',
      header: 'Carrier',
      sortable: true,
      render: (row) => row.carrierVendor ?? '—',
    },
    {
      key: 'accessType',
      header: 'Access Type',
      sortable: true,
      width: '110px',
      render: (row) => row.accessType ?? '—',
    },
    {
      key: 'bandwidthMbps',
      header: 'Bandwidth',
      sortable: true,
      width: '100px',
      render: (row) => row.bandwidthMbps ? `${row.bandwidthMbps} Mbps` : '—',
    },
    {
      key: 'lifecycleStatus',
      header: 'Status',
      sortable: true,
      width: '140px',
      render: (row) => (
        <StatusBadge
          label={row.lifecycleStatus}
          variant={LIFECYCLE_VARIANTS[row.lifecycleStatus] ?? 'gray'}
        />
      ),
    },
  ];

  // Conditionally add MRC column for roles that can see financials
  if (canSeeFinancials) {
    columns.push({
      key: 'mrc',
      header: 'MRC',
      sortable: true,
      width: '100px',
      render: (row) => row.mrc != null ? `$${Number(row.mrc).toLocaleString()}` : '—',
    });
  }

  const [circuitView, setCircuitView] = useState<'list' | 'bandwidth'>('list');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Circuits</h1>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 mb-5 border-b border-border">
        <button
          onClick={() => setCircuitView('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${circuitView === 'list' ? 'border-baker-red text-baker-red' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Circuit List
        </button>
        <button
          onClick={() => setCircuitView('bandwidth')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${circuitView === 'bandwidth' ? 'border-baker-red text-baker-red' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Bandwidth Analytics
        </button>
      </div>

      {circuitView === 'bandwidth' ? (
        <BandwidthAnalyticsView />
      ) : (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {canWrite && (
            <button
              onClick={() => navigate('/admin/import')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              Import Circuits
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: '0.8rem', color: '#64748b' }}>
          Site:
          <input
            type="text"
            value={siteFilter}
            onChange={(e) => { setSiteFilter(e.target.value); setParams((prev) => ({ ...prev, page: 1 })); }}
            placeholder="e.g. 001"
            style={{ marginLeft: '0.5rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem', width: '60px' }}
          />
        </label>

        <label style={{ fontSize: '0.8rem', color: '#64748b' }}>
          Carrier:
          <input
            type="text"
            value={carrierFilter}
            onChange={(e) => { setCarrierFilter(e.target.value); setParams((prev) => ({ ...prev, page: 1 })); }}
            placeholder="e.g. Lumen"
            style={{ marginLeft: '0.5rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem', width: '100px' }}
          />
        </label>

        <label style={{ fontSize: '0.8rem', color: '#64748b' }}>
          Status:
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setParams((prev) => ({ ...prev, page: 1 })); }}
            style={{ marginLeft: '0.5rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
          >
            <option value="">All</option>
            <option value="Ordered">Ordered</option>
            <option value="In Progress">In Progress</option>
            <option value="Installed">Installed</option>
            <option value="Active">Active</option>
            <option value="Pending Disconnect">Pending Disconnect</option>
            <option value="Disconnected">Disconnected</option>
          </select>
        </label>

        <label style={{ fontSize: '0.8rem', color: '#64748b' }}>
          Type:
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setParams((prev) => ({ ...prev, page: 1 })); }}
            style={{ marginLeft: '0.5rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
          >
            <option value="">All</option>
            <option value="MPLS">MPLS</option>
            <option value="DIA">DIA</option>
            <option value="Broadband">Broadband</option>
            <option value="Point-to-Point">Point-to-Point</option>
            <option value="SD-WAN">SD-WAN</option>
            <option value="LTE">LTE</option>
          </select>
        </label>
      </div>

      {error ? (
        <ErrorMessage
          message={(error as Error).message ?? 'Failed to load circuits.'}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <LoadingSpinner message="Loading circuits…" />
      ) : (
        <DataTable
          columns={columns}
          data={(data?.data ?? []) as (DataCircuit & Record<string, unknown>)[]}
          rowKey={(row) => String(row.circuitId)}
          totalItems={data?.total}
          page={params.page}
          pageSize={params.pageSize}
          onPageChange={handlePageChange}
          onSort={handleSort}
          onSearch={handleSearch}
          searchPlaceholder="Search circuits…"
          emptyMessage="No circuits found."
        />
      )}
      </>
      )}
    </div>
  );
}
